import { getDollarValue } from 'lib/api/cmf';
import { getUfValue } from 'lib/api/cmf';
import { hasAnySettingsPermissions } from 'lib/permissionChecks/settings';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
} from 'react-router-dom';

import Layout from './layouts/layout';
import {
  hasAnyPermissions,
  validateCompanyTypes,
  validatePermissions,
} from './lib/functions/utils';
import { hasAnyFinancingPermission } from './lib/permissionChecks';
import Assistant from './pages/Assistant';
import BusinessProfile from './pages/BusinessProfile';
import Summary from './pages/BusinessProfile/FinancialStatements/Components/Summary';
import SIIProfile from './pages/BusinessProfile/SII';
import Consultor from './pages/Consultor';
import CreditHubProEvaluations from './pages/Evaluations';
import Finance from './pages/Finance';
import FactoringRequests from './pages/Finance/Components/Financing';
import FinancialStatementsAssign from './pages/FinancialStatementsAssign';
import Funds from './pages/Funds';
import Business from './pages/Funds/Components/Business';
import Home from './pages/Home';
import Integrations from './pages/Integrations';
import MyAccounts from './pages/MyAccounts';
import FinancialStatementsEmptyer from './pages/MyFinancialStatements';
import Contacts from './pages/Network';
import Notifications from './pages/Notifications';
import PruffRushSyncs from './pages/PruffRush';
import ConfirmQuote from './pages/PublicLandings/ConfirmQuote';
import ConfirmUserRegistrationAdmin from './pages/PublicLandings/ConfirmUseRegistrationAdmin';
import EnrolationForm from './pages/PublicLandings/EnrolationForm';
import EnrolationFormV2 from './pages/PublicLandings/EnrolationFormV2';
import PaymentLink from './pages/PublicLandings/PaymentLink';
import InvoiceInformation from './pages/PublicLandings/PaymentLink/components/InvoiceInformation';
import Settings from './pages/Settings';
import DecisionTreeCreate from './pages/Settings/Components/Risk/Components/DecisionEngines/components/Create';
import UserProfile from './pages/UserProfile';
import { AuthState } from './services/redux/authSlice';

const AppRoutes: React.FC = () => {
  const dispatch = useDispatch();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  getDollarValue(dispatch);
  getUfValue(dispatch);

  return (
    <Router>
      <Routes>
        <Route
          path="/"
          element={
            <Layout>
              <Home />
            </Layout>
          }
        />
        <Route path="/factoring/simulation/:code" element={<ConfirmQuote />} />
        <Route path="/submitCredentials/:id" element={<EnrolationForm />} />
        <Route path="/enrollment/:id" element={<EnrolationFormV2 />} />
        <Route
          path="/home/:page"
          element={
            <Layout>
              <Home />
            </Layout>
          }
        />
        <Route
          path="/profile"
          element={
            <Layout>
              <UserProfile />
            </Layout>
          }
        />
        <Route
          path="/companies/:page"
          element={
            <Layout>
              <MyAccounts />
            </Layout>
          }
        />
        <Route
          path="/consultor"
          element={
            <Layout>
              <Consultor />
            </Layout>
          }
        />
        {validateCompanyTypes(user, 'Integrations') &&
          validatePermissions(user, 'integrations_manage') && (
            <Route
              path="/integrations/"
              element={
                <Layout>
                  <Integrations />
                </Layout>
              }
            />
          )}
        {validateCompanyTypes(user, 'Integrations') &&
          validatePermissions(user, 'integrations_manage') && (
            <Route
              path="/integrations/:integration"
              element={
                <Layout>
                  <Integrations />
                </Layout>
              }
            />
          )}
        <Route
          path="/confirmRegistration/createPassword/:code"
          element={<ConfirmUserRegistrationAdmin />}
        />
        <Route
          path="/payment/:encryptedCompanyId/:encryptedBusinessIdentifier"
          element={<PaymentLink />}
        />
        <Route
          path="/payment/:encryptedCompanyId/:encryptedBusinessIdentifier/:invoiceId"
          element={<InvoiceInformation />}
        />
        {(validatePermissions(user, 'financing_visibility_create') ||
          validatePermissions(user, 'financing_visibility_recieve') ||
          validatePermissions(user, 'financing_factoring_recieve') ||
          validatePermissions(user, 'financing_factoring_review') ||
          validatePermissions(user, 'financing_factoring_sent')) && (
          <Route
            path="/financing/:page"
            element={
              <Layout>
                <FactoringRequests />
              </Layout>
            }
          />
        )}
        <Route
          path="/companies/:id/:mainPage/:page"
          element={
            <Layout>
              <BusinessProfile />
            </Layout>
          }
        />
        <Route
          path="/companies/temporary/:id/:mainPage/:page"
          element={
            <Layout>
              <SIIProfile />
            </Layout>
          }
        />
        {validatePermissions(user, 'financial_states_read') && (
          <Route
            path="/financialStatements/business/:id/financial/assginations/:financialStatementId/edit"
            element={
              <Layout>
                <FinancialStatementsAssign />
              </Layout>
            }
          />
        )}
        {validatePermissions(user, 'financial_states_read') && (
          <Route
            path="/financialStatements/business/:id/financial/assginations/results"
            element={
              <Layout>
                <Summary emptyer />
              </Layout>
            }
          />
        )}
        {(validateCompanyTypes(user, 'FinancingHub') ||
          validateCompanyTypes(user, 'AccountantCollections') ||
          validateCompanyTypes(user, 'FactoringQuoting')) &&
          hasAnyFinancingPermission(user) && (
            <Route
              path="/finance/:mainPage/:subPage"
              element={
                <Layout>
                  <Finance />
                </Layout>
              }
            />
          )}
        {(validateCompanyTypes(user, 'FinancingHub') ||
          validateCompanyTypes(user, 'AccountantCollections') ||
          validateCompanyTypes(user, 'FactoringQuoting')) &&
          hasAnyFinancingPermission(user) && (
            <Route
              path="/finance/:mainPage"
              element={
                <Layout>
                  <Finance />
                </Layout>
              }
            />
          )}
        {validatePermissions(user, 'financial_states_create_assignment') && (
          <Route
            path="/financialStatements/:page"
            element={
              <Layout>
                <FinancialStatementsEmptyer />
              </Layout>
            }
          />
        )}
        {validateCompanyTypes(user, 'CreditHubPro') &&
          validatePermissions(user, 'settings_risk') && (
            <Route
              path="/settings/engines/create"
              element={
                <Layout>
                  <DecisionTreeCreate />
                </Layout>
              }
            />
          )}
        {validateCompanyTypes(user, 'CreditHubPro') &&
          validatePermissions(user, 'settings_risk') && (
            <Route
              path="/settings/engines/edit/:id"
              element={
                <Layout>
                  <DecisionTreeCreate />
                </Layout>
              }
            />
          )}
        {hasAnySettingsPermissions(user) && (
          <Route
            path="/settings/:mainPage/:subPage"
            element={
              <Layout>
                <Settings />
              </Layout>
            }
          />
        )}
        {(validateCompanyTypes(user, 'FinancingHub') ||
          validateCompanyTypes(user, 'Accountant')) &&
          hasAnyPermissions(user, [
            'network_view',
            'sii_read_profile',
            'tgr_read',
          ]) && (
            <Route
              path="/network/:page"
              element={
                <Layout>
                  <Contacts />
                </Layout>
              }
            />
          )}
        {validateCompanyTypes(user, 'CreditHubPro') &&
          validatePermissions(user, 'creditHubPro_evaluation_view') && (
            <Route
              path="/creditHubPro/:mainPage/:subPage"
              element={
                <Layout>
                  <CreditHubProEvaluations />
                </Layout>
              }
            />
          )}
        {validateCompanyTypes(user, 'PruffRush') && (
          <Route
            path="/sync"
            element={
              <Layout>
                <PruffRushSyncs />
              </Layout>
            }
          />
        )}
        {
          <Route
            path="/asisstant"
            element={
              <Layout>
                <Assistant />
              </Layout>
            }
          />
        }
        {
          <Route
            path="/notifications"
            element={
              <Layout>
                <Notifications />
              </Layout>
            }
          />
        }
        {validateCompanyTypes(user, 'Fund') && (
          <Route
            path="/funds/:page"
            element={
              <Layout>
                <Funds />
              </Layout>
            }
          />
        )}
        {validateCompanyTypes(user, 'Fund') && (
          <Route
            path="/funds/business/:companyId/:businessIdentifier/:page"
            element={
              <Layout>
                <Business />
              </Layout>
            }
          />
        )}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Router>
  );
};

export default AppRoutes;
